import { store } from '../lib/store';
import { DRIVER_TYPE_APP, get_driver_type } from '../lib/driver-state';
import { driver_path, is_driver_flow_path, is_driver_offers_path } from '../lib/driver-paths';
import { CLIENT_ENTRY_PATH } from '../lib/client-paths';
import { EV_ENTRY_PATH, is_ev_flow_path } from '../lib/ev-paths';
import { APP_BRAND_MARK_SRC, APP_BRAND_NAME, APP_BRAND_WORDMARK_DARK_SRC, APP_BRAND_WORDMARK_SRC } from '../lib/brand';
import { is_rede_mode, rede_display_name } from '../lib/rede-context';
import { escapeHtml } from '../lib/utils';
import { can_offer_install_shortcut, open_install_cta } from './install-banner';
import { render_theme_menu } from '../lib/theme';
import { bind_legal_links, render_legal_menu } from '../lib/legal';

type NavbarBrandMode = 'app' | 'driver' | 'station' | 'rede';
type StationBrand = { name: string; logo: string };

let navbar_store_sync_ready = false;
let navbar_resize_observer: ResizeObserver | null = null;
let observed_navbar: HTMLElement | null = null;
let page_station_brand: (StationBrand & { path: string }) | null = null;
let chat_account: { authenticated: boolean; open: (trigger: HTMLElement) => void } | null = null;

export function set_navbar_chat_account(account: typeof chat_account): void {
    chat_account = account;
    mount_navbar();
}

function current_page_station_brand(): StationBrand | null {
    return page_station_brand?.path === window.location.pathname ? page_station_brand : null;
}

function station_brand(): StationBrand {
    return current_page_station_brand() || {
        name: String(store.get('station_name') || '').trim(),
        logo: String(store.get('station_logo') || '').trim(),
    };
}

// Marca pública da página, sem trocar o CNPJ, o PIN ou a sessão de compra.
export function set_navbar_station_brand(brand: StationBrand | null): void {
    page_station_brand = brand ? { ...brand, path: window.location.pathname } : null;
    mount_navbar();
}

function sync_navbar_offset(navbar: HTMLElement): void {
    const height = Math.ceil(navbar.getBoundingClientRect().height);
    if (height <= 0) return;
    document.body.style.paddingTop = `${height}px`;
}

function observe_navbar_height(navbar: HTMLElement): void {
    sync_navbar_offset(navbar);
    if (typeof ResizeObserver === 'undefined') return;
    if (!navbar_resize_observer) {
        navbar_resize_observer = new ResizeObserver(() => {
            const current_navbar = document.getElementById('navbar');
            if (current_navbar) sync_navbar_offset(current_navbar);
        });
    }
    if (observed_navbar === navbar) return;
    navbar_resize_observer.disconnect();
    observed_navbar = navbar;
    navbar_resize_observer.observe(navbar);
}

function clean_cnpj(value: unknown): string {
    return (typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint' ? String(value) : '').replace(/\D/g, '');
}

function has_driver_brand_context(in_driver_flow: boolean, driver_type: number): boolean {
    const vehicle = String(store.get('driver_veiculo') || '').trim();
    const plate = String(store.get('driver_placa') || '').trim();
    return in_driver_flow && driver_type === DRIVER_TYPE_APP && !!(vehicle || plate);
}

function has_station_brand_context(in_driver_flow: boolean, in_ev_flow: boolean): boolean {
    const cnpj = clean_cnpj(store.get('cnpj'));
    const station_name = String(store.get('station_name') || '').trim();
    const station_logo = String(store.get('station_logo') || '').trim();
    return !in_driver_flow && !in_ev_flow && cnpj.length === 14 && !!(station_name || station_logo);
}

function driver_brand_label(show_driver_brand: boolean): string {
    const plate = String(store.get('driver_placa') || '').trim();
    const eyebrow = plate ? `Mot App · ${plate}` : 'Mot App';
    if (show_driver_brand) {
        const safe_eyebrow = escapeHtml(eyebrow);
        return `
      <span class="navbar-brand__content navbar-brand__content--driver">
        <span class="navbar-brand__eyebrow">${safe_eyebrow}</span>
      </span>
    `;
    }

    return '';
}

function station_brand_label(show_station_brand: boolean): string {
    const station_name = station_brand().name;
    if (!show_station_brand || !station_name) return '';

    const safe_title = escapeHtml(station_name);
    return `
    <span class="navbar-brand__content navbar-brand__content--station">
      <span class="navbar-brand__title" title="${safe_title}">${safe_title}</span>
    </span>
  `;
}

function rede_brand_label(show_rede_brand: boolean): string {
    return show_rede_brand ? rede_brand_title(rede_display_name()) : '';
}

// Marca da rede na barra. As prévias do painel montam a mesma marcação com a rede do bot.
export function rede_brand_title(name: string): string {
    const safe_title = escapeHtml(name);
    return `
    <span class="navbar-brand__content navbar-brand__content--station">
      <span class="navbar-brand__title" title="${safe_title}">${safe_title}</span>
    </span>
  `;
}

export function rede_brand_mark(name: string, logo: string): string {
    return `
      <span class="navbar-brand__mark navbar-brand__mark--station navbar-brand__mark--rede">
        ${
            logo ?
                `<img src="${escapeHtml(logo)}" alt="" class="navbar-brand__mark-image navbar-brand__mark-image--tile" />`
            :   `<span class="navbar-brand__initial">${escapeHtml(brand_initial(name))}</span>`
        }
      </span>
    `;
}

function brand_initial(value: string): string {
    return String(value || 'R').trim().slice(0, 1).toUpperCase() || 'R';
}

function brand_media(mode: NavbarBrandMode): string {
    if (mode === 'driver') {
        const safe_name = escapeHtml(APP_BRAND_NAME);
        return `
      <span class="navbar-brand__mark">
        <img src="${APP_BRAND_MARK_SRC}" alt="${safe_name}" class="navbar-brand__mark-image" />
      </span>
    `;
    }

    if (mode === 'station') {
        const station = station_brand();
        const station_name = station.name || 'Posto';
        const station_logo = station.logo;
        const safe_name = escapeHtml(station_name);
        const safe_logo = escapeHtml(station_logo);
        return `
      <span class="navbar-brand__mark navbar-brand__mark--station">
        ${
            station_logo ?
                `<img src="${safe_logo}" alt="${station.name ? '' : safe_name}" class="navbar-brand__mark-image" />`
            :   `<span class="navbar-brand__initial">${escapeHtml(brand_initial(station_name))}</span>`
        }
      </span>
    `;
    }

    if (mode === 'rede') return rede_brand_mark(rede_display_name(), String(store.get('rede_logo') || '').trim());

    const safe_name = escapeHtml(APP_BRAND_NAME);
    return `
    <span class="navbar-brand__wordmark">
      <img src="${APP_BRAND_WORDMARK_SRC}" alt="${safe_name}" class="navbar-brand__wordmark-image navbar-brand__wordmark-image--light" />
      <img src="${APP_BRAND_WORDMARK_DARK_SRC}" alt="${safe_name}" class="navbar-brand__wordmark-image navbar-brand__wordmark-image--dark" />
    </span>
  `;
}

function driver_nav_icon(): string {
    return `
    <span class="driver-nav-settings-icon" aria-hidden="true">
      <i class="fa fa-id-card" aria-hidden="true"></i>
    </span>
  `;
}

export function render_navbar(): string {
    const path = window.location.pathname;
    const in_ai_preview = path === '/ai';
    const in_driver_flow = is_driver_flow_path(path);
    const in_ev_flow = is_ev_flow_path(path);
    const in_driver_offers = is_driver_offers_path(path);
    const has_phone = !!store.get('phone');
    const has_driver_phone = !!store.get('driver_phone');
    const driver_type = get_driver_type();
    const show_rede_brand = (in_driver_flow || in_ai_preview) && is_rede_mode();
    const show_driver_brand = has_driver_brand_context(in_driver_flow, driver_type);
    const show_station_brand = !show_rede_brand && (in_ai_preview
        ? !!current_page_station_brand()
        : has_station_brand_context(in_driver_flow, in_ev_flow));
    const brand_mode: NavbarBrandMode =
        show_rede_brand ? 'rede'
        : show_driver_brand ? 'driver'
        : show_station_brand ? 'station'
        : 'app';
    const driver_href = has_driver_phone ? driver_path('offers', path) : driver_path('entry', path);
    const brand_href =
        in_ai_preview ? '/ai'
        : in_driver_flow ? driver_href
        : in_ev_flow ? EV_ENTRY_PATH
        : CLIENT_ENTRY_PATH;
    const brand_class =
        brand_mode === 'driver' ? ' navbar-brand--driver'
        : brand_mode === 'station' ? ' navbar-brand--station'
        : ' navbar-brand--logo';
    const show_install = !in_ai_preview && can_offer_install_shortcut();
    return `
    <nav id="navbar" class="navbar glass-navbar band-wash-host fixed-top">
      <span class="band-wash band-wash--shell" aria-hidden="true">
        <span class="band-wash__art"></span>
        <span class="band-wash__soft"></span>
        <span class="band-wash__scrim"></span>
      </span>
      <div class="container-fluid px-3 px-sm-4">
        <a class="navbar-brand d-flex align-items-center gap-2${brand_class}" href="${brand_href}" data-link>
          ${brand_media(brand_mode)}
          ${driver_brand_label(show_driver_brand)}
          ${station_brand_label(show_station_brand)}
          ${rede_brand_label(show_rede_brand)}
        </a>
        <div class="d-flex align-items-center gap-1 gap-sm-2">
          ${
              show_install ?
                  `
            <button id="install-app-nav-btn" class="btn btn-link nav-link px-2" title="Criar atalho">
              <i class="fas fa-download"></i><span class="navbar-btn-label"> Instalar</span>
            </button>
          `
              :   ''
          }
          ${
              in_driver_offers && has_driver_phone && driver_type !== DRIVER_TYPE_APP ?
                  `
            <button
              id="driver-scan-nav-btn"
              class="btn btn-link nav-link px-2 driver-nav-settings-btn"
              title="Ativar motorista de app">
              ${driver_nav_icon()}<span class="navbar-btn-label"> Motorista App</span>
            </button>
          `
              :   ''
          }
          ${
              has_phone && !in_driver_flow && !in_ev_flow ?
                  `
            <button id="change-phone-nav-btn" class="btn btn-link nav-link px-2" title="Trocar telefone">
              <i class="fas fa-right-from-bracket"></i><span class="navbar-btn-label"> Desconectar</span>
            </button>
          `
              :   ''
          }
          ${in_ai_preview ? `<div class="navbar-preferences" role="group" aria-label="Tema, documentos e conta">
            ${render_theme_menu()}${render_legal_menu()}
            <button type="button" id="chat-account-nav" class="chat-account-nav" ${chat_account ? '' : 'disabled'}
              title="${chat_account?.authenticated ? 'Conectado. Abrir sua conta' : 'Entrar na conta'}"
              aria-label="${chat_account?.authenticated ? 'Conectado. Abrir sua conta' : 'Entrar na conta'}">
              <i class="fas ${chat_account?.authenticated ? 'fa-user-check' : 'fa-user'}" aria-hidden="true"></i>
            </button>
          </div>` : render_theme_menu()}
        </div>
      </div>
    </nav>`;
}

export function mount_navbar(): void {
    if (!navbar_store_sync_ready) {
        navbar_store_sync_ready = true;
        const rerender = () => mount_navbar();
        store.on('cnpj', rerender);
        store.on('phone', rerender);
        store.on('station_name', rerender);
        store.on('station_logo', rerender);
        store.on('rede_mode', rerender);
        store.on('rede_nome', rerender);
        store.on('rede_logo', rerender);
        store.on('driver_phone', rerender);
        store.on('driver_placa', rerender);
        store.on('driver_veiculo', rerender);
    }
    const el = document.getElementById('navbar');
    if (el) el.outerHTML = render_navbar();
    const mounted_navbar = document.getElementById('navbar');
    if (mounted_navbar) {
        observe_navbar_height(mounted_navbar);
        bind_legal_links(mounted_navbar);
    }
    document.getElementById('change-phone-nav-btn')?.addEventListener('click', () => {
        void window.__change_phone?.();
    });
    document.getElementById('install-app-nav-btn')?.addEventListener('click', () => {
        void open_install_cta();
    });
    document.getElementById('driver-scan-nav-btn')?.addEventListener('click', () => {
        window.__driver_scan_qr?.();
    });
    document.getElementById('chat-account-nav')?.addEventListener('click', event => {
        if (event.currentTarget instanceof HTMLElement) chat_account?.open(event.currentTarget);
    });
}

export function set_active_nav(_path: string): void {}
